import type { FieldValues, Path, UseFormClearErrors, UseFormSetValue } from 'react-hook-form';

import type { EventBus } from '../../../../helpers';
import type { BaseFormEvents } from '../types';

export class UIService<T extends FieldValues> {
  constructor(
    private getIsDisplayingCard: () => boolean,
    private getIsDirty: () => boolean,
    private setIsDirty: (isDirty: boolean) => void,
    private setIsDisplayingCard: (isDisplayingCard: boolean) => void,
    private setIsLoading: (isLoading: boolean) => void,
    private eventBus: EventBus<BaseFormEvents<T>>
  ) {}

  public api() {
    return {
      getCanGetFormValue: () => {
        return Boolean(this.getFormValuesFn);
      },

      getCanSetFormValue: () => {
        return Boolean(this.setFormValueFn);
      },

      getCanClearErrors: () => {
        return Boolean(this.clearFormErrorsFn);
      },

      triggerValidation: async () => {
        if (!this.triggerValidation) throw new Error('form trigger validation function is not added');

        return this.triggerValidation();
      },

      clearErrors: () => this.clearFormErrorsFn?.(),

      isFormDirty: () => this.getIsDirty(),

      setIsLoading: (value: boolean) => this.setIsLoading(value),

      setFormValues: (newValues: T) => {
        if (this.getIsDisplayingCard()) {
          return false;
        }

        const setValueFn = this.setFormValueFn;

        if (!setValueFn) throw new Error('set form values function is not added');

        Object.keys(newValues).forEach((key) => {
          setValueFn(key as Path<T>, newValues[key]);
        });

        return true;
      },
    };
  }

  public data() {
    return {};
  }

  public handlers() {
    return {
      addGetFormValues: (getValues: (payload?: string | string[]) => T) => {
        this.getFormValuesFn = getValues;
      },

      addSetFormValue: (setValue: UseFormSetValue<T>) => {
        this.setFormValueFn = setValue;
      },

      addClearErrors: (clearErrors: UseFormClearErrors<T>) => {
        this.clearFormErrorsFn = clearErrors;
      },

      addTriggerValidation: (trigger: () => Promise<boolean>) => {
        this.triggerValidation = trigger;
      },

      onEditClick: () => this.setIsDisplayingCard(false),

      onCancelClick: () => {
        this.setIsDisplayingCard(true);
        this.setIsDirty(false);

        this.eventBus.emit('formDirtyStateChanged', false, { source: 'user' });

        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      },
    };
  }

  public getFormValuesFn: ((payload?: string | string[]) => T) | null = null;

  public setFormValueFn: UseFormSetValue<T> | null = null;

  public clearFormErrorsFn: UseFormClearErrors<T> | null = null;

  public triggerValidation: (() => Promise<boolean>) | null = null;
}
